// The safety screen (Stephanie 2026-11-02). Before this, anything that broke
// while a page was drawing left a completely blank page — twice in one day —
// with no clue what happened. Now the page shows "Something went wrong on this
// page" with a Reload button, and the error is sent to the server
// (POST /web-crash → server log + audit event "web.crash") so it can be found
// and fixed without screenshots. Wraps the whole app; moving to another page
// clears it (keyed by path in main.tsx).
import { Component, type ErrorInfo, type ReactNode } from "react";
import { api } from "@/lib/api";

/** Pure: the report sent for a crash, capped to what the server accepts. */
export function crashReport(error: unknown, componentStack: string | null | undefined, path: string, build: string | null) {
  const e = error instanceof Error ? error : new Error(String(error));
  return {
    message: (e.message || "Unknown error").slice(0, 2000),
    stack: (e.stack ?? "").slice(0, 8000),
    componentStack: (componentStack ?? "").slice(0, 8000),
    path: path.slice(0, 500),
    build: build ? build.slice(0, 80) : null,
  };
}

export class SafetyScreen extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    const build = typeof __BUILD_SHA__ === "string" ? __BUILD_SHA__ : null;
    const report = crashReport(error, info.componentStack, window.location.pathname + window.location.search, build);
    // Best effort: a failed report must never cause a second crash.
    void api("/web-crash", { method: "POST", body: JSON.stringify(report), skipAuthRefresh: true }).catch(() => undefined);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="flex min-h-screen items-center justify-center bg-canvas px-6">
        <div className="max-w-md rounded-md border border-rule bg-white p-8 text-center">
          <h1 className="text-xl font-semibold tracking-[-0.4px] text-ink">Something went wrong on this page.</h1>
          <p className="mt-2 text-sm text-muted">
            Your work up to the last save is kept. We've been sent the details so it can be fixed.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button type="button" onClick={() => window.location.reload()} className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-white">
              Reload page
            </button>
            <a href="/" className="rounded-md border border-rule px-4 py-2 text-sm font-medium text-ink hover:bg-cream-100">
              Go to the home page
            </a>
          </div>
        </div>
      </div>
    );
  }
}
